;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { context, trace } from '@opentelemetry/api'

import { createLogPipeline } from './create-log-pipeline'
import type { RuntimeBindingsProvider } from './runtime-bindings'
import type {
  LogContext,
  LogInput,
  LogLevel,
  LogMethod,
  Transport,
} from './types'

/**
 * General interface for a logger
 *
 * Can be used to inject a logger into a component or a service
 */
export type Logger = Record<LogLevel, LogMethod>

export function createLogger({
  environment,
  version,
  transports,
  minLevel = 'info',
  name,
  runtimeBindingsProviders = [],
}: {
  /** The deployment environment tag to use for the logger */
  environment: 'prod' | 'test' | 'dev'
  /** Version appended to the logs
   * @example process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA
   */
  version?: string
  /**
   * Provide transports for the logger
   *
   * Each item is optional to allow conditionally adding a transport:
   * @example [consoleTransport, otelTransport]
   */
  transports: Array<Transport | undefined>
  /** Minimum level to send to transport */
  minLevel?: LogLevel
  /** Name of the logger */
  name: string
  /** Additional runtime bindings to add to the log payload */
  runtimeBindingsProviders?: RuntimeBindingsProvider[]
}): Logger {
  const logLevelRanks: Record<LogLevel, number> = {
    error: 40,
    warn: 30,
    info: 20,
    debug: 10,
  }

  const activeTransports = transports.filter(
    (t): t is Transport => t !== undefined,
  )

  const logPipeline = createLogPipeline({
    staticBindings: {
      env: environment,
      version,
      logger: { name },
    },
    runtimeBindingsProviders: [
      {
        name: 'currentTimestamp',
        getBindings: () => ({ timestamp: new Date().toISOString() }),
      },
      {
        name: 'otel',
        getBindings: () => {
          const otel = {
            trace_id: trace.getSpan(context.active())?.spanContext().traceId,
            span_id: trace.getSpan(context.active())?.spanContext().spanId,
          }

          return { otel }
        },
      },
      ...runtimeBindingsProviders,
    ],
  })

  /** connects the log pipeline to the transport */
  async function log(level: LogLevel, input: LogInput, context?: LogContext) {
    if (logLevelRanks[level] < logLevelRanks[minLevel]) {
      return
    }

    const payload = await logPipeline(input, context)
    for (const transport of activeTransports) {
      transport[level](payload)
    }
  }

  return {
    error: (input, context) => log('error', input, context),
    warn: (input, context) => log('warn', input, context),
    info: (input, context) => log('info', input, context),
    debug: (input, context) => log('debug', input, context),
  }
}
